import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { HelpPopover } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IMailDeliveryRow, IMailTemplate } from '../../api/mail-template-contracts';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { LoadError, TableSkeleton, useAdminData } from '../../components/admin-page';
import { mailLogPath } from '../../paths';
import { surveyLabel } from './template-fields';

/**
 * The template's Delivery (SR-523 stage 3), beside the page as History is: its mails counted per
 * survey variant and version — sent, failed, skipped, bounced, complaints, unsubscribed — each
 * row a link to the Email log filtered to those sends. Read when it opens; survey-api counts.
 */

export function DeliverySheet({
  open,
  onOpenChange,
  template
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: IMailTemplate;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-[min(26rem,calc(100vw-2rem))] gap-0 p-0"
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-mail-delivery=""
      >
        <SheetHeader className="px-5 pt-5 pb-4">
          <SheetTitle className="flex items-center gap-1 text-[0.9375rem] text-[var(--color-royal-blue)]">
            Delivery
            <HelpPopover label="About delivery" className="size-6">
              <p>
                Every mail of this template the worker handled, by survey and version. Skipped mails
                were never sent: the address unsubscribed, there was none, or the environment
                withheld it. Bounced counts the addresses the provider could not deliver to.
              </p>
            </HelpPopover>
          </SheetTitle>
          <SheetDescription>{template.name}, by survey and version.</SheetDescription>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto size-8"
            aria-label="Close delivery"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </SheetHeader>
        {open && <DeliveryRows code={template.code} />}
      </SheetContent>
    </Sheet>
  );
}

function DeliveryRows({ code }: { code: string }) {
  const read = useAdminData((signal) => mailTemplatesApi.delivery(code, signal), [code]);
  if (read.error) {
    return (
      <div className="px-5">
        <LoadError message={read.error} />
      </div>
    );
  }
  if (!read.data) {
    return (
      <div className="px-5">
        <TableSkeleton rows={3} />
      </div>
    );
  }
  if (read.data.length === 0) {
    return <p className="px-5 text-[0.8125rem] text-muted-foreground">Nothing sent with this template yet.</p>;
  }
  return (
    <ul className="grid content-start gap-2 overflow-y-auto p-3 text-[0.8125rem]" aria-label="Sends by version">
      {read.data.map((row) => (
        <li key={`${row.survey ?? 'base'}:${row.version}`} data-delivery-row={`${row.survey ?? 'base'}:${row.version}`}>
          <Link
            to={mailLogPath({ template: code, survey: row.survey ?? undefined, version: row.version })}
            className="grid gap-1.5 rounded-md border border-border px-3 py-2 outline-none hover:border-[var(--color-teal)] focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex items-center gap-2">
              <span className="font-medium text-[var(--color-royal-blue)]">
                {surveyLabel(row.survey)} · version {row.version}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {row.lastSentAt ? `last ${formatDateTime(row.lastSentAt)}` : 'none sent'}
              </span>
              <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden />
            </span>
            <Counts row={row} />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Counts({ row }: { row: IMailDeliveryRow }) {
  const cells: [string, number, boolean][] = [
    ['Sent', row.sent, false],
    ['Failed', row.failed, row.failed > 0],
    ['Skipped', row.skipped, false],
    ['Bounced', row.bounced, row.bounced > 0],
    ['Complaints', row.complaints, row.complaints > 0],
    ['Unsubscribed', row.unsubscribed, false]
  ];
  return (
    <dl className="grid grid-cols-3 gap-x-3 gap-y-0.5">
      {cells.map(([label, value, attention]) => (
        <div key={label} className="flex items-baseline justify-between gap-2">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className={attention ? 'font-medium text-[var(--color-deep-magenta)] tabular-nums' : 'tabular-nums'}>
            {value.toLocaleString('en-US')}
          </dd>
        </div>
      ))}
    </dl>
  );
}
